import { CMYKColor, RGBColor } from '@company-box/models';

// Naive, device-independent CMYK<->RGB conversion - a cosmetic approximation for the color
// picker's CMYK input mode, not a color-managed (ICC) conversion. It will not match what a print
// vendor's real CMYK press output looks like; it exists so the picker can offer CMYK sliders while
// everything downstream (Fabric, three.js, CSS) keeps working in hex/RGB as it always has.
export function cmykToHex(cmyk: CMYKColor): string {
  const c = cmyk.c / 100;
  const m = cmyk.m / 100;
  const y = cmyk.y / 100;
  const k = cmyk.k / 100;
  const toChannel = (value: number) => {
    const channel = Math.round(255 * (1 - value) * (1 - k));
    return Math.min(255, Math.max(0, channel))
      .toString(16)
      .padStart(2, '0');
  };
  return `#${toChannel(c)}${toChannel(m)}${toChannel(y)}`;
}

export function hexToCmyk(hex: string): CMYKColor {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const k = 1 - Math.max(r, g, b);
  if (k >= 1) {
    return { c: 0, m: 0, y: 0, k: 100 };
  }
  const c = (1 - r - k) / (1 - k);
  const m = (1 - g - k) / (1 - k);
  const y = (1 - b - k) / (1 - k);
  const toPercent = (value: number) => Math.round(value * 100);
  return { c: toPercent(c), m: toPercent(m), y: toPercent(y), k: toPercent(k) };
}

// Plain RGB<->hex conversion - exact, unlike cmykToHex()/hexToCmyk() above, since hex already is
// RGB (just re-encoded), not a different color space being approximated.
export function rgbToHex(rgb: RGBColor): string {
  const toChannel = (value: number) =>
    Math.min(255, Math.max(0, Math.round(value)))
      .toString(16)
      .padStart(2, '0');
  return `#${toChannel(rgb.r)}${toChannel(rgb.g)}${toChannel(rgb.b)}`;
}

export function hexToRgb(hex: string): RGBColor {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

// Normalizes any CSS color string to 6-digit hex. Needed because Fabric.js objects report an unset
// fill/stroke as the CSS default 'rgb(0, 0, 0)' rather than hex, so a color picker seeded straight
// from a fabric object (object.fill/object.stroke) can otherwise receive that literal string and
// display it verbatim instead of a hex value. Also expands a 3-digit hex shorthand and tolerates a
// missing leading "#" (e.g. from a plain-text hex input); anything else unparseable falls back to
// black rather than showing a broken swatch.
export function cssColorToHex(value: string): string {
  const trimmed = (value ?? '').trim();
  const rgbMatch = trimmed.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*[\d.]+\s*)?\)$/i);
  if (rgbMatch) {
    return rgbToHex({ r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) });
  }
  let hex = trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  return /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#000000';
}
